import { Button, Skeleton } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { errorCode } from '../personnel/source.js';
import { CategoryTree } from './CategoryTree.js';
import classes from './Documents.module.css';
import { FolderTreeScreen } from './FolderTreeEditor.js';
import type { DocumentSource } from './source.js';
import type { LibraryTree } from './types.js';

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; tree: LibraryTree };

/**
 * Compliance ▸ ISM Forms ▸ Form Categories (plan M8a Task 17, M8a4 Task 16; ADR 0037 §11, ADR 0038
 * §8): ISM Forms'un klasör ağacı. Ağaç şablondan gelir (Standard v1: 01. Safety Management System
 * … 07. ISM Forms). Web ofiste gemi seçicili düzenleyici (`documents.tree`), gemide salt okunur.
 */
export function FormCategoriesScreen({ source }: { source: DocumentSource }) {
  // Web ofiste gemi seçicili düzenleyici (ADR 0038 §8); gemide salt okunur ağaç
  if (source.office?.folders) return <FolderTreeScreen section="ism" source={source} />;
  return <ReadOnlyCategories source={source} />;
}

/** Gemide Form Categories: geminin ağacı salt okunur (ADR 0037 §7; gemi ağaç yazamaz). */
function ReadOnlyCategories({ source }: { source: DocumentSource }) {
  const { t } = useTranslation();
  const k = (key: string) => t(`documents.categories.${key}`);
  const [state, setState] = useState<State>({ kind: 'loading' });

  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', tree: await source.tree('ism') });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source]);

  useEffect(() => {
    setState({ kind: 'loading' });
    void load();
  }, [load]);

  let body: ReactNode;
  if (state.kind === 'error') {
    body = (
      <Panel>
        <EmptyState
          tone="danger"
          title={t('documents.screen.error')}
          description={t(`documents.errors.${state.code}`, { defaultValue: '' })}
          action={
            <Button
              size="xs"
              variant="light"
              leftSection={<IconRefresh size={14} />}
              onClick={() => void load()}
            >
              {t('documents.screen.retry')}
            </Button>
          }
        />
      </Panel>
    );
  } else if (state.kind === 'loading') {
    body = (
      <Panel title={k('caption')}>
        <div aria-busy="true" className={classes.catSkeleton}>
          <span className={classes.srOnly}>{t('documents.screen.loading')}</span>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={20} width={i % 2 ? '58%' : '72%'} />
          ))}
        </div>
      </Panel>
    );
  } else if (state.tree.folders.length === 0) {
    body = (
      <Panel title={k('caption')}>
        <EmptyState title={k('empty')} description={k('readOnly')} />
      </Panel>
    );
  } else {
    body = <CategoryTree section="ism" tree={state.tree} />;
  }

  return (
    <div className={classes.catScreen}>
      <PageHeader title={k('title')} description={k('subtitle')} />
      {body}
    </div>
  );
}
